import Duration from 'dayjs/plugin/duration'
import RelativeTime from 'dayjs/plugin/relativeTime'
import get from 'lodash-es/get'
import isNull from 'lodash-es/isNull'
import isUndefined from 'lodash-es/isUndefined'

import { DISPLAY_DATE_FORMAT } from '/@/constants/common'
import { isEmpty, isValidDate } from '/@/utils/is'
import dayjs from '/@/utils/lib/dayjs'

export type TimeUnit =
  | 'milliseconds'
  | 'seconds'
  | 'minutes'
  | 'hours'
  | 'days'
  | 'weeks'
  | 'months'
  | 'years'

/**
 * Because of simplifier usage same date format into overall app
 * @param date
 * @param format default: `DD MMM YYYY HH:mm`
 */
export function formatDate(
  date?: Date | string,
  format = DISPLAY_DATE_FORMAT,
): string | undefined {
  if (!date) return
  return dayjs(date).format(format)
}

/**
 * Makes the difference between the date given as a parameter and the current date readable.
 * Function outputs like "12 hours ago, a day ago" etc.
 * If the date is not valid (e.g. it came text), return the text as is. E.g. (Incoming data => 'CONFIDENTIAL', Output => 'CONFIDENTIAL')
 * @param date
 * @returns string
 */
export function calculateRelativeTimeFormat(date?: string) {
  // If date is not valid, return itself.

  if (!isValidDate(date)) return date

  dayjs.extend(RelativeTime)
  return dayjs(date).fromNow()
}

/**
 * @param count {number}
 * @param unit {TimeUnit}
 * @example count 60, unit 'minutes' returns An hour
 * @example count 60, unit 'months' returns 5 years
 */
export function humanizeTime(count: number, unit: TimeUnit = 'minutes') {
  dayjs.extend(Duration)
  dayjs.extend(RelativeTime)
  return dayjs.duration(count, unit).humanize()
}

/**
 * Generates a grid template area from an array
 * @param array
 * @example ['a', 'b', 'c'] returns '"a" "b" "c"'
 * @example ['a b c', 'd e f', 'g h i'] returns '"a b c" "d e f" "g h i"'
 */
export function generateArrayToGridTemplateArea(array: string[]) {
  // Generate a grid template area from an array
  return array.map((item) => `"${item}"`).join(' ')
}

/**
 * @method formatBadgeCount
 * @param {number|undefined} count Holds count
 * @description Method to format badge count in case of count does not exist.
 * @returns string|number
 */
export function formatBadgeCount(count?: number): string | number {
  if (isUndefined(count)) return 'N/A'
  if (count === 0) return 0

  return count
}

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 *
 * @return Formatted string.
 */
export function humanizeFileSize(bytes: number) {
  const thresh = 1024

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B'
  }

  const units = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  let u = -1

  do {
    bytes /= thresh
    u += 1
  } while (Math.round(Math.abs(bytes)) >= thresh && u < units.length - 1)

  return bytes.toFixed(0) + ' ' + units[u]
}

/**
 * Format isoDate.
 *
 * @param isoDate Date format like yyy-MM-dd'T'HH:mm:ss. SSSXXX.
 * @return Formatted date.
 * @example May 24, 2024
 */
export function formatDateWithMonthName(isoDate: string | Date): string {
  const date = new Date(isoDate)
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
  return new Intl.DateTimeFormat('en-US', options).format(date)
}

/**
 * @method formatNullData
 * @param {null} data Hold data.
 * @description Method to format null values as 'N/A'. Otherwise it returns data itself.
 */
export function formatNullData(data: null) {
  if (isNull(data)) return 'N/A'
  return data
}

export function formatEmptyData(data: any) {
  if (isEmpty(data)) return 'N/A'
  return data
}

/**
 * @method formatNetworkTerms
 * @param {string} value Holds value.
 * @description Method to format network terms correctly.
 */
export function formatNetworkTerms(value: string) {
  const termFormats = {
    ip_address: 'IP Address',
    dns: 'DNS',
    ip: 'IP',
    asn: 'ASN',
    http: 'HTTP',
    starttls: 'Start TLS',
    start_tls: 'Start TLS',
    dnssec: 'DNSSEC',
    tls: 'TLS',
    as: 'AS',
    ssl: 'SSL',
    bgp_prefix: 'BGP Prefix',
    ns_records: 'NS Records',
    as_name: 'AS Name',
    mx: 'MX',
  }

  return get(termFormats, value) || value
}

/**
 * Gets time information from iso date format
 * @param isoDate Date format like yyy-MM-dd'T'HH:mm:ss. SSSXXX.
 * @return Formatted hour.
 * @example 09:22
 */
export function formatTimeAsHour(isoDate: any) {
  const date = new Date(isoDate)
  return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
}

/**
 * @name convertSecondsToHourMinFormat
 * @params seconds, format ('short' or 'long')
 * @description Get second value, return hour,min,second format.
 * @example '10 min 20 second'
 */
export const convertSecondsToHourMinFormat = (
  seconds: any,
  format: 'short' | 'long' = 'short',
) => {
  if (seconds === 0) return '0'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.round(seconds % 60)

  const formatTimePart = (value: number, unit: string, plural: string) => {
    if (value === 0) return ''
    return format === 'long'
      ? `${value} ${unit}${value > 1 ? plural : ''}`
      : `${value}${unit.charAt(0)}`
  }

  const hourPart = formatTimePart(hours, 'hour', 's')
  const minutePart = formatTimePart(minutes, 'min', 's')
  const secondPart = formatTimePart(secs, 'second', 's')

  return [hourPart, minutePart, secondPart].filter(Boolean).join(' ').trim()
}

/**
 * @name formatSecondsAsTime
 * @params seconds
 * @description Get second value, return day, hour, min, second format.
 * @example '10d 09:55:56' or '09:55:56'
 */
export const formatSecondsAsTime = (seconds: number): string => {
  if (isEmpty(seconds)) return '-'

  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.round(seconds % 60)

  // Format the hours, minutes, and seconds to always be two digits
  const formattedHours = String(hours).padStart(2, '0')
  const formattedMinutes = String(minutes).padStart(2, '0')
  const formattedSeconds = String(secs).padStart(2, '0')

  if (days > 0) {
    return `${days}d ${formattedHours}:${formattedMinutes}:${formattedSeconds}`
  }
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
}

// TODO: improve this function @sena
/**
 * @method formatWithConfig
 * @description This function searches for an item in the config object based on the provided value.
 * If a matching item is found, it returns the label of that item.
 * If no match is found, it returns undefined.
 * @param value - The value to search for within the config object.
 * @param  config - The configuration object that contains items. config.value should be an array.
 */
export function formatWithConfig(value: string, config: any): string | undefined {
  const foundItem = config.value.find((item: any) => item.value === value)
  return foundItem ? foundItem.label : undefined
}

/**
 * @method formatNumber
 * @param {number} value - Number to format
 * @param {string} type - Type of formatting: 'currency' for money, 'number' for regular numbers
 * @param {string} locale - Locale for formatting (default: 'tr-TR')
 * @description Formats numbers with appropriate separators based on type.
 * For currency: uses comma as decimal separator (Turkish standard)
 * For numbers: uses dot as thousand separator
 * @example formatNumber(1234.56, 'currency') // "1.234,56"
 * @example formatNumber(1234567, 'number') // "1.234.567"
 */
export function formatNumber(
  value?: number,
  type: 'currency' | 'number' = 'number',
  locale: string = 'en-US',
): string {
  if (isEmpty(value)) return '0'

  if (type === 'currency') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(value!)
      .replace('$', '')
      .trim()
  }

  return new Intl.NumberFormat(locale, {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value!)
}
